import type { AxiosRequestConfig } from 'axios';
import { v2ParamsSerializer } from '@stuut/common/src/api_client';
import { BankStatement, type BankStatementAttachment, type PresignedUrl } from '../models';
import { ModelResource, type PaginatedResult } from './resource';

export class BankStatements extends ModelResource<BankStatement> {
  model = BankStatement;
  resourcePath = 'v2/bank-statements';

  async list(args?: AxiosRequestConfig, paginatedResult?: false): Promise<BankStatement[]>;

  async list(
    args?: AxiosRequestConfig,
    paginatedResult?: true,
  ): Promise<PaginatedResult<BankStatement>>;

  async list(
    args?: AxiosRequestConfig,
    paginatedResult?: boolean,
  ): Promise<BankStatement[] | PaginatedResult<BankStatement>> {
    const params = args?.params as Record<string, unknown> | undefined;

    const response = await this.api.get(this.resourcePath, {
      ...args,
      params,
      paramsSerializer: v2ParamsSerializer,
    });
    const responseData = response.data as PaginatedResult<BankStatement>;

    // Deserialize to model instances
    const serializedData = responseData.data.map((json: any) => BankStatement.fromJson(json));

    // Return based on paginatedResult parameter
    if (paginatedResult) {
      return { ...responseData, data: serializedData } as PaginatedResult<BankStatement>;
    }

    return serializedData;
  }

  async approve(statement: BankStatement): Promise<BankStatement> {
    return this.api.post(this.path(statement.id, 'approve'), {}).then((res) => {
      statement.updateFromJson(res.data);
      return statement;
    });
  }

  async unapprove(statement: BankStatement): Promise<BankStatement> {
    return this.api.post(this.path(statement.id, 'unapprove'), {}).then((res) => {
      statement.updateFromJson(res.data);
      return statement;
    });
  }

  async upload(
    accountId: string,
    fileName: string,
    mimeType: string,
  ): Promise<{
    presignedUrl: PresignedUrl;
    statement: BankStatement;
    attachment: BankStatementAttachment;
  }> {
    const response = await this.api.post(this.path('upload'), {
      data: {
        account_id: accountId,
        filename: fileName,
        mime_type: mimeType,
      },
    });

    const { statement, presigned_url } = response.data;
    const bankStatement = BankStatement.fromJson(statement);
    return {
      presignedUrl: presigned_url,
      statement: bankStatement,
      attachment: bankStatement.attachment,
    };
  }
}
